// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useState } from 'react';

import {
  type Product,
  type ProductId,
  type MessagePriority,
  type MessageTypeId,
  type BulkRecipientCode,
  type Company,
  type CompanyId,
  type User,
  type UserId,
  useEvolveGet,
  useEvolvePaginatedGet,
  useEvolvePost,
} from '@evolvemep/api-ts';
import {
  Button,
  Flex,
  getThemeColor,
  Radio,
  Stack,
  Stepper,
  Tabs,
  Text,
  Textarea,
  useMantineTheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { HTMLRenderer } from 'app/MessageCenter/HTMLRenderer';
import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { SearchableMultiSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableMultiSelect';
import { isNotNil } from 'helpers/isNotNil';
import { useUser } from 'hooks/user/useUser';

const recipientTypeOptions = [
  { label: 'All users', value: 'All users' },
  { label: 'Company admins', value: 'Company admins' },
  { label: 'By company', value: 'By company' },
  { label: 'By product', value: 'By product' },
  { label: 'Custom user list', value: 'Custom' },
] as const;

type RecipientType = (typeof recipientTypeOptions)[number]['value'];
type TabOptions = 'Source' | 'Preview';

const MAX_MESSAGE_LENGTH = 65000;

const CreateMessageModalContents = ({ onClose }: { onClose: () => void }) => {
  const [active, setActive] = useState(0);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<MessagePriority | null>(null);
  const [selectedMessageTypeId, setSelectedMessageTypeId] = useState<MessageTypeId | null>(null);
  const [recipientProducts, setRecipientProducts] = useState<Product[]>([]);
  const [recipientCompanies, setRecipientCompanies] = useState<Company[]>([]);
  const [activeTab, setActiveTab] = useState<TabOptions>('Source');
  const [source, setSource] = useState('');
  const htmlError = source.length > MAX_MESSAGE_LENGTH;
  const [recipientType, setRecipientType] = useState<RecipientType | null>(null);
  const [recipients, setRecipients] = useState<User[]>([]);
  const { data: messageTypes } = useEvolveGet('admin/messageType', { lazy: false });

  const { user } = useUser();
  const theme = useMantineTheme();
  const activeTabColor = getThemeColor(theme.primaryColor, theme);

  const nextStep = () => setActive((curr) => (curr < 2 ? curr + 1 : curr));
  const prevStep = () => setActive((curr) => (curr > 0 ? curr - 1 : curr));

  const { apiCall, loading } = useEvolvePost('admin/message');

  const sendingTo = ():
    | { bulkRecipientCode: BulkRecipientCode }
    | { recipients: UserId[] }
    | { recipientProductIds: ProductId[] }
    | { recipientCompanyIds: CompanyId[] } => {
    if (recipientType === 'Custom') return { recipients: recipients.map((u) => u.userId) };
    if (recipientType === 'By product') {
      return { recipientProductIds: recipientProducts.map(({ productId }) => productId) };
    }
    if (recipientType === 'By company') {
      return { recipientCompanyIds: recipientCompanies.map(({ companyId }) => companyId) };
    }
    return {
      bulkRecipientCode: recipientType === 'Company admins' ? 'ADMIN' : 'ALL',
    };
  };

  const sendMessage = () => {
    const recipientsObj = sendingTo();
    void apiCall({
      ...recipientsObj,
      messageTypeId: selectedMessageTypeId,
      companyId: user.companyId,
      fromUserId: user.userId,
      fromName: `${user.userFirstName} ${user.userLastName}`,
      messagePriority: priority ?? 'NORMAL',
      subject: title,
      body: source,
    }).then(() => {
      notifications.show({
        title: 'Success',
        message: 'Message successfully sent',
        color: 'green',
      });
      onClose();
    });
  };

  const isValid = () => {
    if (active === 0 && isNotNil(title) && title !== '' && isNotNil(priority) && isNotNil(selectedMessageTypeId)) {
      return true;
    }
    if (active === 1 && isNotNil(source)) return true;
    if (active === 2 && isNotNil(recipientType)) {
      if (recipientType === 'Custom') return recipients.length > 0;
      if (recipientType === 'By product') return recipientProducts.length > 0;
      return true;
    }
    return false;
  };

  const paginatedGetUsers = useEvolvePaginatedGet('admin/user');
  const paginatedGetProducts = useEvolvePaginatedGet('admin/product');
  const paginatedGetCompanies = useEvolvePaginatedGet('admin/company');

  return (
    <>
      <Stepper
        active={active}
        styles={{
          content: { flex: 1, display: 'flex', flexDirection: 'column' },
          root: { flex: 1, display: 'flex', flexDirection: 'column' },
        }}
      >
        <Stepper.Step label="Message type">
          <Stack>
            <TextInputDebounced
              placeholder="Title"
              description="128 character limit"
              onChange={setTitle}
              value={title}
              maxLength={129}
              error={title && title.length > 128}
            />
          </Stack>
          <Text fw={600} mt="xl">
            Select message type
          </Text>
          <Radio.Group
            onChange={(v) => setSelectedMessageTypeId(v as MessageTypeId)}
            withAsterisk
            value={selectedMessageTypeId}
          >
            {messageTypes && messageTypes.length > 0 ? (
              messageTypes.map(({ messageTypeId, messageTypeName }) => (
                <Radio key={messageTypeId} value={messageTypeId} label={messageTypeName} m="xs" />
              ))
            ) : (
              <Radio disabled label="Loading" m="xs" />
            )}
          </Radio.Group>
          <Text fw={600} mt="xl">
            Select priority
          </Text>
          <Text c="gray" size="xs">
            High-priority messages will display in both a pop-up banner and the Message Center. Normal priority messages
            will only display in the Message Center.
          </Text>
          <Radio.Group onChange={(v) => setPriority(v as MessagePriority)} withAsterisk value={priority as string}>
            <Radio value="NORMAL" label="Normal" m="xs" />
            <Radio value="HIGH" label="High" m="xs" />
          </Radio.Group>
        </Stepper.Step>
        <Stepper.Step label="Add source">
          <Stack flex={1}>
            <Tabs
              value={activeTab}
              onChange={(e) => {
                setActiveTab((t) => (e ?? t) as typeof activeTab);
              }}
              styles={{
                root: { flex: 1, display: 'flex', flexDirection: 'column' },
                panel: { flex: 1, display: 'flex', flexDirection: 'column', height: '100%' },
                tab: {
                  width: 110,
                  height: 50,
                  fontWeight: 600,
                },
                tabLabel: { fontWeight: 600 },
              }}
            >
              <Tabs.List>
                <Tabs.Tab
                  value="Source"
                  style={{ flex: 1, color: activeTab === 'Source' ? activeTabColor : theme.colors.gray[6] }}
                >
                  Source
                </Tabs.Tab>
                <Tabs.Tab
                  value="Preview"
                  style={{ flex: 1, color: activeTab === 'Preview' ? activeTabColor : theme.colors.gray[6] }}
                >
                  Preview
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="Source" py="xs">
                <Textarea
                  description={
                    source.length <= MAX_MESSAGE_LENGTH
                      ? `${(MAX_MESSAGE_LENGTH - source.length).toLocaleString()} characters remaining`
                      : `${(source.length - MAX_MESSAGE_LENGTH).toLocaleString()} characters over the limit`
                  }
                  onChange={(e) => setSource(e.currentTarget.value)}
                  autosize
                  descriptionProps={{
                    c: htmlError ? 'red' : undefined,
                  }}
                  minRows={8}
                  maxRows={16}
                  value={source}
                  error={htmlError}
                />
              </Tabs.Panel>
              <Tabs.Panel value="Preview" py="xs" mih={220}>
                <HTMLRenderer
                  html={source}
                  allowScripts
                  style={{
                    width: '100%',
                    flex: 1,
                  }}
                />
              </Tabs.Panel>
            </Tabs>
          </Stack>
        </Stepper.Step>
        <Stepper.Step label="Recipients">
          <Text fw={600} mt="xl">
            Select recipient type
          </Text>
          <Radio.Group
            onChange={(v) => setRecipientType(v as RecipientType)}
            withAsterisk
            mb="md"
            value={recipientType as string}
          >
            {recipientTypeOptions.map(({ label, value }) => (
              <Radio key={value} m="xs" value={value} label={label} />
            ))}
          </Radio.Group>
          {recipientType === 'Custom' && (
            <SearchableMultiSelect
              label="Select users"
              paginatedGet={paginatedGetUsers}
              value={recipients}
              onChange={setRecipients}
              getItemLabel={(u) => u.userEmail}
              clearSearchOnChange
              idKey="userId"
              clearable
              nowrap={false}
            />
          )}
          {recipientType === 'By product' && (
            <SearchableMultiSelect
              label="Select products"
              paginatedGet={paginatedGetProducts}
              value={recipientProducts}
              onChange={setRecipientProducts}
              getItemLabel={(p) => p.productName}
              clearSearchOnChange
              idKey="productId"
              clearable
              nowrap={false}
            />
          )}
          {recipientType === 'By company' && (
            <SearchableMultiSelect
              label="Select companies"
              paginatedGet={paginatedGetCompanies}
              value={recipientCompanies}
              onChange={setRecipientCompanies}
              getItemLabel={(c) => c.companyName}
              clearSearchOnChange
              idKey="companyId"
              clearable
              nowrap={false}
            />
          )}
        </Stepper.Step>
      </Stepper>
      <Flex justify="flex-end" gap="md">
        <Button
          variant="light"
          onClick={() => {
            if (active > 0) prevStep();
            else onClose();
          }}
        >
          {active > 0 ? 'Back' : 'Cancel'}
        </Button>
        <Button
          onClick={() => {
            if (active < 2 && isValid()) nextStep();
            else sendMessage();
          }}
          disabled={!isValid() || htmlError}
          loading={loading}
        >
          {active < 2 ? 'Next' : 'Send message'}
        </Button>
      </Flex>
    </>
  );
};

export const CreateMessageModal = () => {
  const { roles } = useUser();
  const [opened, setOpened] = useState(false);

  // Don't use `userHasRoles` since Admins don't have access to this,
  // *only* MarketingManagers have access.
  const canCreateMessage = useMemo(() => roles.includes('Evolve.MarketingManager'), [roles]);

  if (!canCreateMessage) return null;

  return (
    <>
      <Button size="sm" leftSection={<EvolveIcon icon="Add" size="sm" />} onClick={() => setOpened(true)}>
        Create message
      </Button>
      <WrappedModal
        title="New message"
        size="85%"
        bodyProps={{
          mih: '80vh',
          display: 'flex',
          style: { flexDirection: 'column' },
        }}
        opened={opened}
        onClose={() => setOpened(false)}
        padding="md"
        hideModalButtons
        closeOnConfirm={false}
      >
        <CreateMessageModalContents onClose={() => setOpened(false)} />
      </WrappedModal>
    </>
  );
};
